import React, { useMemo, useState } from 'react';
import { MdGrade, MdTrendingUp, MdTrendingDown, MdFactCheck } from 'react-icons/md';
import { Card, StatCard, Badge, EmptyState } from '../../ui/Card';
import { gradeBand, shortDate } from '../../ui/format';
import { useStudentData } from '../StudentDataContext';
import './pages.css';

export default function Grades() {
  const { grades } = useStudentData();
  const [subject, setSubject] = useState('all');

  const subjects = useMemo(() => {
    const seen = new Map();
    grades.forEach((g) => { if (g.subject_title) seen.set(g.subject_title, true); });
    return [...seen.keys()];
  }, [grades]);

  const rows = useMemo(() => {
    const list = subject === 'all' ? grades : grades.filter((g) => g.subject_title === subject);
    // The API returns oldest-first (ORDER BY e.date); a pupil wants the
    // newest result at the top.
    return [...list].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [grades, subject]);

  const stats = useMemo(() => {
    const scored = rows.filter((g) => g.percentage !== null && g.percentage !== undefined);
    if (!scored.length) return null;
    const pcts = scored.map((g) => Number(g.percentage));
    return {
      average: Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length),
      highest: Math.round(Math.max(...pcts)),
      lowest: Math.round(Math.min(...pcts)),
      passed: scored.filter((g) => Number(g.percentage) >= 50).length,
      total: scored.length,
    };
  }, [rows]);

  return (
    <div className="page">
      <h1 className="page-title">العلامات</h1>

      {stats && (
        <div className="stat-grid">
          <StatCard icon={MdGrade} tone="primary" value={`${stats.average}%`} label="المعدل" />
          <StatCard icon={MdTrendingUp} tone="success" value={`${stats.highest}%`} label="أعلى علامة" />
          <StatCard icon={MdTrendingDown} tone="warning" value={`${stats.lowest}%`} label="أدنى علامة" />
          <StatCard icon={MdFactCheck} tone="info" value={`${stats.passed}/${stats.total}`} label="ناجح" />
        </div>
      )}

      {subjects.length > 1 && (
        <div className="chip-row">
          <button
            type="button"
            className={`chip${subject === 'all' ? ' is-active' : ''}`}
            onClick={() => setSubject('all')}
          >
            كل المواد
          </button>
          {subjects.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${subject === s ? ' is-active' : ''}`}
              onClick={() => setSubject(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <Card title="نتائج الامتحانات" icon={MdGrade} padded={false}>
        {rows.length === 0 ? (
          <EmptyState icon={MdGrade} title="لا توجد علامات بعد" hint="ستظهر النتائج هنا فور رصدها." />
        ) : (
          <ul className="row-list">
            {rows.map((g) => {
              const pct = g.percentage === null || g.percentage === undefined ? null : Math.round(Number(g.percentage));
              const band = pct === null ? null : gradeBand(pct);
              return (
                <li key={g.exam_id} className="row">
                  <div className={`score-ring score-${band ? band.tone : 'neutral'}`}>
                    {pct === null ? '—' : `${pct}%`}
                  </div>
                  <div className="row-main">
                    <div className="row-title">{g.subject_title || g.exam_title}</div>
                    <p className="row-sub">
                      {g.exam_type_title || g.exam_title}
                      {g.date && ` • ${shortDate(g.date)}`}
                    </p>
                  </div>
                  <div className="row-end">
                    <span className="row-score">{g.marks_obtained} / {g.total_marks}</span>
                    {band && <Badge tone={band.tone}>{band.label}</Badge>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
